import { type CollisionBox, type CollisionIndex } from "./collision";

export interface LabelPlacement {
	direction: string;
	dx: number;
	dy: number;
}

export interface MarkerLabelLayoutResult {
	startX: number;
	startY: number;
	textX: number;
	textAlign: CanvasTextAlign;
	placementIndex: number;
}

interface MarkerLabelLayoutInput {
	x: number;
	y: number;
	blockWidth: number;
	blockHeight: number;
	placements: LabelPlacement[];
	cachedIndex: number | undefined;
	markerId: string;
	drawOrder: number;
	collisionPadding: number;
	tree: CollisionIndex;
	/** When true, reuse the cached placement without collision checks or inserting into the tree. */
	skipCollision?: boolean;
	/** When true, always place at index 0 without checking for collisions, but still insert into the tree so others respect this label. */
	forcePlace?: boolean;
}

function buildLayout(input: MarkerLabelLayoutInput, placementIndex: number): MarkerLabelLayoutResult {
	const { direction, dx, dy } = input.placements[placementIndex];
	const startY = direction === "up" ? input.y + dy - input.blockHeight
		: direction === "down" ? input.y + dy
			: input.y + dy - input.blockHeight / 2;
	const startX = direction === "left" ? input.x + dx - input.blockWidth
		: (direction === "up" || direction === "down") ? input.x + dx - input.blockWidth / 2
			: input.x + dx;

	return {
		startX,
		startY,
		textX: input.x + dx,
		textAlign: direction === "left" ? "right"
			: (direction === "up" || direction === "down") ? "center" : "left",
		placementIndex,
	};
}

export function getMarkerLabelPlacements(iconWidth: number, iconHeight: number, markerAnchor: string): LabelPlacement[] {
	const gap = 4;
	const halfW = iconWidth / 2 + gap;
	const halfH = iconHeight / 2 + gap;
	const placements = [
		{ direction: "right", dx: halfW, dy: 0 },
		{ direction: "left", dx: -halfW, dy: 0 },
		{ direction: "up", dx: 0, dy: -halfH },
		{ direction: "down", dx: 0, dy: halfH },
	];

	const opposite: Record<string, string> = {
		"right": "left",
		"left": "right",
		"bottom": "up",
		"top": "down",
		"bottom-right": "left",
		"bottom-left": "right",
		"top-right": "left",
		"top-left": "right",
	};

	const preferred = opposite[markerAnchor];
	if (preferred) {
		const index = placements.findIndex((placement) => placement.direction === preferred);
		if (index > 0) placements.unshift(...placements.splice(index, 1));
	}

	return placements;
}

export function resolveMarkerLabelLayout(input: MarkerLabelLayoutInput): MarkerLabelLayoutResult | undefined {
	if (input.skipCollision) {
		const placementIndex = input.cachedIndex ?? 0;
		return buildLayout(input, placementIndex);
	}

	const indices = input.cachedIndex != null
		? [input.cachedIndex, ...input.placements.map((_, index) => index).filter((index) => index !== input.cachedIndex)]
		: input.placements.map((_, index) => index);

	for (const placementIndex of indices) {
		const layout = buildLayout(input, placementIndex);

		const labelBox: CollisionBox = {
			minX: layout.startX - input.collisionPadding,
			minY: layout.startY - input.collisionPadding,
			maxX: layout.startX + input.blockWidth + input.collisionPadding,
			maxY: layout.startY + input.blockHeight + input.collisionPadding,
			markerId: input.markerId,
			isLabel: true,
			drawOrder: input.drawOrder,
		};
		if (!input.forcePlace && input.tree.collides(labelBox)) continue;

		input.tree.insert(labelBox);

		return layout;
	}
}